import cloneDeep from 'lodash/cloneDeep'
import { arrayOrEmpty } from '@utils/util-helpers'

export const LO_ONBOARDING_SCENARIO_DELAYS = Object.freeze({
  load: 500,
  start: 700,
  complete: 600,
})

const sampleUrl = {
  url: 'https://mottomortgage.com/',
  displayText: 'Motto website',
}

const samplePost = {
  id: 920,
  title: 'Loan Originator Onboarding Guide',
  postType: 'media',
  isDownloadable: true,
}

function activity(id, name, overrides = {}) {
  return {
    id,
    activity: name,
    title: name,
    description: `${name} — complete this before moving on to the next activity.`,
    assignedTo: 'Loan Originator',
    timeToComplete: 3,
    dateToComplete: null,
    dateCompleted: null,
    submissionStatus: 'pending',
    stepOrder: overrides.stepOrder,
    urls: [],
    posts: [],
    ...overrides,
  }
}

const BEGINNER_STEPS = [
  {
    order: 1,
    title: 'Step 1: Get set up in MottoCenter',
    isCompletionOrdered: true,
    activities: [
      activity(910001, 'Upload a profile picture', { stepOrder: 1, urls: [sampleUrl] }),
      activity(910002, 'Confirm your contact details', { stepOrder: 1, posts: [samplePost] }),
    ],
  },
  {
    order: 2,
    title: 'Step 2: Complete LO Champions',
    isCompletionOrdered: false,
    activities: [
      activity(910003, 'Register for LO Champions', { stepOrder: 2, urls: [sampleUrl] }),
      activity(910004, 'Practice in the wemlo sandbox', { stepOrder: 2 }),
    ],
  },
]

const EXPERT_STEPS = [
  {
    order: 1,
    title: 'Step 1: Get set up in MottoCenter',
    isCompletionOrdered: false,
    activities: [activity(920001, 'Upload a profile picture', { stepOrder: 1, urls: [sampleUrl] })],
  },
  {
    order: 2,
    title: 'Step 2: Confirm your tooling',
    isCompletionOrdered: false,
    activities: [activity(920002, 'Confirm the Loan Milestones Journey is active in Spark', { stepOrder: 2 })],
  },
]

const FINISHED_STATUSES = ['accepted', 'completed', 'skipped']

const scenarioState = {
  experience: null,
  steps: [],
}

function stepsForExperience(experience) {
  return cloneDeep(experience === 'Expert' ? EXPERT_STEPS : BEGINNER_STEPS)
}

function isStepCompleted(step) {
  return arrayOrEmpty(step.activities).every((item) => FINISHED_STATUSES.includes(item.submissionStatus))
}

function firstAvailableStep() {
  return scenarioState.steps.find((step) => !isStepCompleted(step)) || null
}

function buildStepsPayload() {
  const availableStep = firstAvailableStep()

  return scenarioState.steps.map((step) => ({
    order: step.order,
    title: step.title,
    isCompletionOrdered: step.isCompletionOrdered,
    isCompleted: isStepCompleted(step),
    isAvailable: Boolean(availableStep) && availableStep.order === step.order,
    activities: cloneDeep(step.activities),
  }))
}

function hasPendingActivities() {
  return scenarioState.steps.some((step) => !isStepCompleted(step))
}

export function resetLoOnboardingScenario() {
  scenarioState.experience = null
  scenarioState.steps = []
}

export function getLoOnboardingStatusResponse() {
  return {
    hasPendingOnboarding: !scenarioState.experience || hasPendingActivities(),
    onboardingType: 'lo',
  }
}

export function getLoOnboardingStepsResponse() {
  if (!scenarioState.experience) {
    return {
      hasPendingOnboarding: true,
      requiresExperienceChoice: true,
      experience: null,
      steps: [],
    }
  }

  return {
    hasPendingOnboarding: hasPendingActivities(),
    requiresExperienceChoice: false,
    experience: scenarioState.experience,
    steps: buildStepsPayload(),
  }
}

export function startLoOnboardingScenario(experience) {
  if (!scenarioState.experience) {
    scenarioState.experience = experience === 'Expert' ? 'Expert' : 'Beginner'
    scenarioState.steps = stepsForExperience(scenarioState.experience)
  }

  return getLoOnboardingStepsResponse()
}

export function completeLoOnboardingScenarioActivity(activityId) {
  const availableStep = firstAvailableStep()
  const target = arrayOrEmpty(availableStep?.activities).find((item) => String(item.id) === String(activityId))

  if (target) {
    target.submissionStatus = 'completed'
    target.dateCompleted = '2026-07-29'
  }

  return {
    activity: target ? cloneDeep(target) : null,
    stepCompleted: availableStep ? isStepCompleted(availableStep) : false,
  }
}

export function waitForLoOnboardingScenarioDelay(type) {
  const delay = LO_ONBOARDING_SCENARIO_DELAYS[type] || 0

  return new Promise((resolve) => setTimeout(resolve, delay))
}
